import { ScormUIDataAdapter } from "./scorm_1_2/ScormUIDataAdapter";
import { Scorm2004UIDataAdapter } from "./scorm_1_3/Scorm2004UIDataAdapter";
import { isTtsApi, setTtsApiAsGlobalApi, createDelegateApi, getGlobalApi, isSequenceConnected } from "./GlobalScormApi";
import { lookupScormApi } from "./common/lookupScormApi";
import { ScormUIData } from "./common/ScormUIData";
import { ScormType } from "./common/ScormType";
import { CourseBase } from "@/course/CourseBase";
import { CourseEvents } from "@/course/CourseEvents";
import { SequenceDataMap } from "@/course/SequenceDataMap";
import { LoggingService } from "@/logging/LoggingService";
import { Assert } from "@/common/Assert";
import { Event } from "@/common/Event";
const logId = "ScormCourse";
export const createScormCourse = function (options) {
    LoggingService.log("check scorm environment...", logId);
    if (isTtsApi(options.lmsApi)) {
        setTtsApiAsGlobalApi(options.lmsApi);
        return options.lmsApi;
    }
    Assert.checkState(options.scoExportType === "singleSco");
    const scormType = getScormType(options.scoExportVersion);
    Assert.checkState(scormType === ScormType.SCORM_1_2 || scormType === ScormType.SCORM_1_3);
    let lmsApi = options.lmsApi;
    if (!lmsApi) {
        LoggingService.log(`lookup for lms api with target version ${scormType}`, logId);
        lmsApi = lookupScormApi(scormType);
        if (!lmsApi) {
            LoggingService.log(`lms scorm api not found!`, logId);
            throw new Error("Cannot create scorm course! lms scorm api not found!");
        }
    }
    return createCourse(lmsApi, scormType, options.masteryScore);
};
function getScormType(scoExportVersion) {
    if (!scoExportVersion) {
        return ScormType.SCORM_1_2;
    }
    switch (scoExportVersion) {
        case "1.3":
        case "1_3":
        case "2004":
            return ScormType.SCORM_1_3;
        default:
            return ScormType.SCORM_1_2;
    }
}
export function createCourse(lmsApi, scormType, masteryScore = 80) {
    LoggingService.log(`creating course for scorm ${scormType} lms...`, logId);
    const scormDelegateApi = createDelegateApi(lmsApi, scormType, masteryScore);
    Assert.checkArgument(scormDelegateApi);
    const profileController = createProfileController(scormDelegateApi);
    Assert.checkArgument(profileController);
    // @ts-ignore
    const courseBase = new CourseBase(scormDelegateApi.TTGetCourseMasteryScore());
    // @ts-ignore
    const sequenceDataMap = new SequenceDataMap();
    const className = scormDelegateApi.TTGetApiType() === ScormType.SCORM_1_3 ? "Scorm2004Course" : "ScormCourse";
    Assert.checkArgument(profileController);
    initEventListener();
    function initEventListener() {
        Event.on(document, CourseEvents.COURSE_REFRESH, refreshStatusDelayed);
        Event.on(document, CourseEvents.SEQUENCE_TERMINATED, refreshStatusDelayed);
    }
    function refreshStatusDelayed() {
        console.log("ScormCourseFactory::refreshStatusDelayed())");
        // set a delay to give the lms time to store and provide the data
        setTimeout(refreshStatus, 1000);
    }
    function refreshStatus() {
        console.log("ScormCourseFactory::refreshStatus()");
        profileController.refreshDataBuffer(sequenceDataMap.getResultMap());
    }
    return {
        // config api
        getClassName: () => className,
        getProfileController: () => profileController,
        setUsername: profileController.setUsername,
        getUsername: profileController.getUsername,
        /**
         * Gets the storage system for the course.
         *
         * @returns {String} "scorm_lms"
         */
        getStorageSystem: () => "scorm_lms",
        /**
         * Gets the storage method for the course.
         *
         * @returns {String} "scorm2004" for a scorm 1.3 course, otherwise "jsapi"
         */
        getStorageMethod: profileController.getStorageMethod,
        getCourseMasteryScore: courseBase.getCourseMasteryScore,
        /**
         * Due to the nature of the session based scorm communication,
         * it is not allowed to start multiple SCOs at the same time.
         *
         * @returns {boolean} false
         */
        isMultiScoLaunchAllowed: () => false,
        useScoLaunchParams: () => true,
        initScoreIDs: (useAssessments) => {
            sequenceDataMap.initScoreIDs(useAssessments);
            const globalApi = getGlobalApi();
            if (globalApi) {
                globalApi.TTSetScoreList(sequenceDataMap.getScoreIDs());
            }
            return "true";
        },
        prepareSequenceLaunch: (id) => {
            const globalApi = getGlobalApi();
            if (globalApi) {
                const objScoInfos = sequenceDataMap.getSequenceInfos();
                globalApi.TTSetScoInfo(objScoInfos);
                globalApi.TTPrepareScoLaunch(id);
            }
            return true;
        },
        onSequenceTerminate() {
        },
        isSequenceConnected: isSequenceConnected,
        initSequenceData: sequenceDataMap.initSequenceData,
        getSequenceData: sequenceDataMap.getSequenceData,
        setSequenceData: sequenceDataMap.setSequenceData,
        getSequenceDataMap: sequenceDataMap.getSequenceDataMap,
        clearSequenceDataMap: sequenceDataMap.clearSequenceDataMap,
        /**
         * The current status will be committed to the lms and
         * the lms session will be terminated.
         */
        terminate: () => {
            const globalApi = getGlobalApi();
            if (globalApi) {
                globalApi.TTCommit();
                globalApi.TTTerminate();
            }
        },
        exit: () => {
            // should be implemented when course uses a close button
        },
        /**
         * @deprecated
         *
         * @return {Boolean} true
         */
        handleBrowserOnTerminate: () => true,
        // core api
        getResultMap: sequenceDataMap.getResultMap,
        getSequenceInfos: sequenceDataMap.getSequenceInfos,
        getScoreIDs: sequenceDataMap.getScoreIDs,
        refreshStatus: refreshStatus
    };
}
function createProfileController(delegateApi) {
    if (delegateApi.TTGetApiType() === ScormType.SCORM_1_3) {
        // @ts-ignore
        return new ScormUIData(new Scorm2004UIDataAdapter(delegateApi), ScormType.SCORM_1_3);
    }
    // @ts-ignore
    return new ScormUIData(new ScormUIDataAdapter(delegateApi), ScormType.SCORM_1_2);
}
